import $ from 'jquery';
import dialogPolyfill from 'dialog-polyfill';
import { debounce } from 'lodash';
import * as bodyScrollLock from 'body-scroll-lock/lib/bodyScrollLock';
import applyAccessRoles from './helpers/apply-access-roles';
import setReturnUrls from './helpers/set-return-urls';
import AccountService from '../_shared/account-service';

/**
 * @desc Manages mobile navigation.
 */
export default class MobileMenu {
  /**
   * @param {Object} options
   * @param {string} options.openButtonSelector
   * @param {string} options.menuModalSelector
   * @param {string} options.closeButtonSelector
   */
  constructor({
    openButtonSelector, menuModalSelector, navSectionSelector, closeButtonSelector,
  } = {}, outerUsage = false) {
    this.$openButton = $(openButtonSelector);
    this.$menuModal = $(menuModalSelector);
    this.$closeButton = this.$menuModal.find(closeButtonSelector);
    this.$nav = this.$menuModal.find(navSectionSelector);
    this.$html = $('html');
    this.$window = $(window);
    this.$lastFocusedElement = null;
    this.outerUsage = outerUsage;

    this.init();
  }

  async init() {
    this.isInitialized = true;

    if (!this.$openButton.length || !this.$menuModal.length || !this.$closeButton.length) {
      this.isInitialized = false;

      return;
    }

    const userData = await AccountService.getUserData();

    applyAccessRoles(this.$menuModal, userData);

    setReturnUrls(this.$menuModal);

    dialogPolyfill.registerDialog(this.$menuModal[0]);

    this.attachEventListeners();
  }

  attachEventListeners() {
    this.$window.on('resize', this.tryToCloseMenu());
    this.$window.on('resize', debounce(() => this.fitMainNavList(), 100));

    this.$openButton.mousedown(() => this.preserveFocus());

    this.$openButton.click(() => this.openMenu());

    this.$closeButton.click(() => {
      this.closeMenu();
    });

    this.$menuModal[0].addEventListener('cancel', (event) => {
      event.preventDefault();
      this.closeMenu();
    });

    this.$nav.on('click', '.js-mobile-submenu-open, .js-mobile-more-open', (event) => {
      const $toggle = $(event.currentTarget);
      const submenu = document.getElementById($toggle.attr('aria-controls'));

      if (!submenu) {
        return;
      }

      event.preventDefault();

      this.$activeMobileSubmenuTrigger = $toggle;
      this.$nav.find('.js-mobile-main-list').prop('hidden', true);
      this.$nav.find('.js-mobile-submenu-panel').prop('hidden', true);
      this.$nav.find('.js-mobile-more-item').prop('hidden', true);
      this.$activeMobilePanel = $(submenu);
      this.animateMobilePanel(this.$activeMobilePanel, 'in');
      $toggle.attr('aria-expanded', 'true');
      this.$menuModal.find('.js-mobile-main-header').prop('hidden', true);
      this.$menuModal.find('.js-mobile-submenu-title').text($toggle.hasClass('js-mobile-more-open')
        ? 'More'
        : $toggle.find('.mc-mobile-menu-primary-list__link-text').text().trim());
      this.$menuModal.find('.js-mobile-submenu-header').prop('hidden', false);
      this.$menuModal.addClass('has-mobile-submenu-open');
      this.$menuModal.find('.js-mobile-submenu-back').trigger('focus');
    });

    this.$nav.on('click', '.js-mobile-more-submenu-open', (event) => {
      event.preventDefault();
      const $toggle = $(event.currentTarget);
      const submenu = document.getElementById($toggle.attr('aria-controls'));

      if (!submenu) {
        return;
      }

      this.$activeMobileMoreSubmenuTrigger = $toggle;
      this.$nav.find('.js-mobile-submenu-panel').prop('hidden', true);
      this.$activeMobilePanel = $(submenu);
      this.animateMobilePanel(this.$activeMobilePanel, 'in');
      $toggle.attr('aria-expanded', 'true');
      this.$menuModal.find('.js-mobile-submenu-title').text(
        $toggle.find('.mc-mobile-menu-primary-list__link-text').text().trim(),
      );
      this.$menuModal.find('.js-mobile-submenu-back').trigger('focus');
    });

    this.$menuModal.on('click', '.js-mobile-submenu-back, .js-mobile-submenu-close', () => {
      if (this.$activeMobileMoreSubmenuTrigger) {
        this.showMoreMenu();
      } else {
        this.showMainMenu();
      }
    });

    this.$menuModal[0].addEventListener('close', () => {
      this.showMainMenu(false);
      bodyScrollLock.enableBodyScroll(this.$nav[0]);

      this.$nav.scrollTop(0);
    });
  }

  tryToCloseMenu() {
    return debounce(() => {
      if (this.$menuModal[0].open && window.matchMedia('(min-width: 1024px)').matches) {
        this.$closeButton.trigger('click');
      }
    }, 200);
  }

  openMenu() {
    this.$menuModal[0].showModal();

    $(document.activeElement).blur();

    bodyScrollLock.disableBodyScroll(this.$nav[0]);
    window.requestAnimationFrame(() => this.fitMainNavList());
  }

  async closeMenu() {
    const dialog = this.$menuModal[0];

    if (!dialog.open || this.isMenuClosing) {
      return;
    }

    this.isMenuClosing = true;

    if (dialog.animate && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      const animation = dialog.animate([
        { transform: window.getComputedStyle(dialog).transform },
        { transform: 'translateX(-100%)' },
      ], {
        duration: 280,
        easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)',
        fill: 'forwards',
      });

      await animation.finished.catch(() => {});
      dialog.close();
      animation.cancel();
    } else {
      dialog.close();
    }

    this.isMenuClosing = false;

    if (this.$lastFocusedElement?.length) {
      this.restoreFocus();
    }
  }

  async showMainMenu(restoreFocus = true) {
    if (restoreFocus && this.$activeMobilePanel) {
      const $activePanel = this.$activeMobilePanel;

      await this.animateMobilePanel($activePanel, 'out');

      if (this.$activeMobilePanel?.[0] === $activePanel[0]) {
        this.resetMainMenu(restoreFocus);
      }

      return;
    }

    this.resetMainMenu(restoreFocus);
  }

  resetMainMenu(restoreFocus) {
    if (this.mobilePanelAnimation) {
      this.mobilePanelAnimation.cancel();
      this.mobilePanelAnimation = null;
    }

    this.$nav.find('.js-mobile-submenu-panel').prop('hidden', true);
    this.$nav.find('.js-mobile-main-list').prop('hidden', false);
    this.$nav.find('.js-mobile-more-open').attr('aria-expanded', 'false');
    this.$menuModal.find('.js-mobile-submenu-header').prop('hidden', true);
    this.$menuModal.find('.js-mobile-main-header').prop('hidden', false);
    this.$menuModal.removeClass('has-mobile-submenu-open');

    if (this.$activeMobileMoreSubmenuTrigger) {
      this.$activeMobileMoreSubmenuTrigger.attr('aria-expanded', 'false');
      this.$activeMobileMoreSubmenuTrigger = null;
    }

    if (this.$activeMobileSubmenuTrigger) {
      this.$activeMobileSubmenuTrigger.attr('aria-expanded', 'false');

      if (restoreFocus) {
        this.$activeMobileSubmenuTrigger.trigger('focus');
      }

      this.$activeMobileSubmenuTrigger = null;
    }

    this.$activeMobilePanel = null;
    window.requestAnimationFrame(() => this.fitMainNavList());
  }

  showMoreMenu() {
    const $activePanel = this.$activeMobilePanel;
    const showMorePanel = () => {
      this.$nav.find('.js-mobile-submenu-panel').prop('hidden', true);
      this.$nav.find('#mobile-nav-more-panel').prop('hidden', false);
      this.$activeMobilePanel = this.$nav.find('#mobile-nav-more-panel');
      this.$nav.find('.js-mobile-more-open').attr('aria-expanded', 'true');
      this.$menuModal.find('.js-mobile-submenu-title').text('More');

      if (this.$activeMobileMoreSubmenuTrigger) {
        this.$activeMobileMoreSubmenuTrigger.attr('aria-expanded', 'false').trigger('focus');
        this.$activeMobileMoreSubmenuTrigger = null;
      }
    };

    if ($activePanel && $activePanel[0].id !== 'mobile-nav-more-panel') {
      this.animateMobilePanel($activePanel, 'out').then(() => {
        if (this.$activeMobilePanel?.[0] === $activePanel[0]) {
          showMorePanel();
        }
      });
    } else {
      showMorePanel();
    }
  }

  animateMobilePanel($panel, direction) {
    const panel = $panel[0];

    if (!panel) {
      return Promise.resolve();
    }

    if (this.mobilePanelAnimation) {
      this.mobilePanelAnimation.cancel();
      this.mobilePanelAnimation = null;
    }

    if (direction === 'in') {
      panel.hidden = false;
    }

    if (!panel.animate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      panel.hidden = direction === 'out';
      return Promise.resolve();
    }

    const keyframes = direction === 'in'
      ? [
        { opacity: 0, transform: 'translateX(100%)' },
        { opacity: 1, transform: 'translateX(0)' },
      ]
      : [
        { opacity: 1, transform: 'translateX(0)' },
        { opacity: 0, transform: 'translateX(100%)' },
      ];

    const animation = panel.animate(keyframes, {
      duration: 240,
      easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)',
      fill: 'forwards',
    });

    this.mobilePanelAnimation = animation;

    return animation.finished.catch(() => {}).then(() => {
      if (this.mobilePanelAnimation !== animation) {
        return;
      }

      if (direction === 'out') {
        panel.hidden = true;
      }

      animation.cancel();
      this.mobilePanelAnimation = null;
    });
  }

  fitMainNavList() {
    const $list = this.$nav.find('.js-mobile-main-list');
    const list = $list[0];

    if (!list || !this.$menuModal[0].open || $list.prop('hidden') || !list.clientHeight) {
      return;
    }

    const $items = $list.children('li').not('.js-mobile-more-item');
    const $moreItem = $list.children('.js-mobile-more-item');
    const $moreButton = this.$nav.find('.js-mobile-more-open');
    const gap = parseFloat(window.getComputedStyle(list).rowGap) || 0;
    let totalItemsHeight = 0;

    $items.prop('hidden', false).each((index, item) => {
      totalItemsHeight += item.getBoundingClientRect().height + (index ? gap : 0);
    });

    $moreItem.prop('hidden', false);
    const moreItemHeight = $moreItem[0].getBoundingClientRect().height;
    $moreItem.prop('hidden', true);

    const hasOverflow = totalItemsHeight > list.clientHeight;
    const availableHeight = hasOverflow
      ? list.clientHeight - moreItemHeight - gap
      : list.clientHeight;
    let usedHeight = 0;
    let overflowStarted = false;

    $items.each((index, item) => {
      const itemHeight = item.getBoundingClientRect().height;
      const nextHeight = usedHeight + itemHeight + (index ? gap : 0);

      if (overflowStarted || nextHeight > availableHeight) {
        $(item).prop('hidden', true);
        overflowStarted = true;
      } else {
        usedHeight = nextHeight;
      }
    });

    const $overflowItems = $items.filter((_, item) => item.hidden);
    const overflowIndexes = new Set($overflowItems.map((_, item) => $(item).data('mobile-nav-item-index')).get());

    this.$nav.find('.js-mobile-more-group').each((_, group) => {
      const itemIndex = $(group).data('mobile-nav-item-index');
      $(group).prop('hidden', !overflowIndexes.has(itemIndex));
    });

    $moreItem.prop('hidden', overflowIndexes.size === 0);
    $moreButton.attr('aria-expanded', 'false');
  }

  preserveFocus() {
    this.$lastFocusedElement = $(document.activeElement);
  }

  restoreFocus() {
    const elementTop = this.$lastFocusedElement.offset().top;
    const elementBottom = elementTop + this.$lastFocusedElement.offset().top;

    const viewportTop = this.$window.scrollTop();
    const viewportBottom = viewportTop + this.$window.height();

    if (elementBottom > viewportTop && elementTop < viewportBottom) {
      this.$lastFocusedElement.focus();
    }
  }
}
